import type { ColumnDef } from "@tanstack/react-table";
import {
  PORTFOLIO_ACCOUNT_STATUS,
  PORTFOLIO_ACCOUNT_STATUS_LABELS,
  PORTFOLIO_ACCOUNT_STATUS_VARIANTS,
} from "@signals/shared";
import { Badge, Button } from "../_common";
import type { SmartTableColumnMeta } from "../_common";
import type { TabRow } from "./portfolio.hooks";
import { formatNumber, numericValue } from "./portfolio-page-formatters";
export const CASHFLOW_COLUMNS: ColumnDef<TabRow>[] = [
  {
    accessorKey: "date",
    header: "Date",
    meta: {
      format: "date",
      filterable: "client",
      filterType: "date-range",
    } satisfies SmartTableColumnMeta,
  },
  { accessorKey: "reason", header: "Reason" },
  {
    accessorKey: "accountId",
    header: "Account",
    cell: ({ row }) => {
      const accountName = row.original["accountName"];
      return typeof accountName === "string" ? accountName : "—";
    },
    meta: {
      filterable: "client",
      filterType: "select-single",
      multiSelect: false,
    } satisfies SmartTableColumnMeta,
  },
  {
    accessorKey: "amount",
    header: "Amount",
    meta: { align: "right", format: "number" } satisfies SmartTableColumnMeta,
    cell: ({ getValue }) => formatNumber(numericValue(getValue())),
  },
  { accessorKey: "currency", header: "Currency" },
  { accessorKey: "note", header: "Note" },
];

export const EXCHANGE_RATE_COLUMNS: ColumnDef<TabRow>[] = [
  { accessorKey: "fromCurrency", header: "From" },
  { accessorKey: "to", header: "To" },
  {
    accessorKey: "rate",
    header: "Rate",
    meta: { align: "right", format: "number" } satisfies SmartTableColumnMeta,
    cell: ({ getValue }) => formatNumber(numericValue(getValue())),
  },
  {
    accessorKey: "date",
    header: "Date",
    meta: {
      format: "date",
      filterable: "client",
      filterType: "date-range",
    } satisfies SmartTableColumnMeta,
  },
  { accessorKey: "source", header: "Source" },
];

export const ACCOUNT_COLUMNS: ColumnDef<TabRow>[] = [
  { accessorKey: "name", header: "Name" },
  { accessorKey: "broker", header: "Broker" },
  { accessorKey: "brokerAccountId", header: "Broker Account" },
  { accessorKey: "type", header: "Type" },
  { accessorKey: "currency", header: "Currency" },
  {
    accessorKey: "status",
    header: "Состояние",
    cell: ({ getValue }) => {
      const value = getValue();
      const status = PORTFOLIO_ACCOUNT_STATUS.find((item) => item === value);
      return status ? (
        <Badge variant={PORTFOLIO_ACCOUNT_STATUS_VARIANTS[status]}>
          {PORTFOLIO_ACCOUNT_STATUS_LABELS[status]["ru-RU"]}
        </Badge>
      ) : "—";
    },
  },
  {
    accessorKey: "closedOn",
    header: "Дата закрытия",
    cell: ({ getValue }) => {
      const value = getValue();
      return typeof value === "string" && value ? value : "—";
    },
  },
  {
    accessorKey: "closureNote",
    header: "Основание",
    cell: ({ getValue }) => {
      const value = getValue();
      return typeof value === "string" && value ? value : "—";
    },
  },
];

/** Accounts catalog columns plus a close action for accounts that were never closed. */
export function buildAccountColumns(
  onCloseAccount: (accountId: string) => void,
  onEditAccount: (accountId: string) => void = () => {},
): ColumnDef<TabRow>[] {
  return [
    ...ACCOUNT_COLUMNS,
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const closedOn = row.original["closedOn"];
        const id = row.original["id"];
        if ((typeof closedOn === "string" && closedOn) || typeof id !== "string") return null;
        return (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onEditAccount(id)}>
              Изменить привязку
            </Button>
            <Button size="sm" variant="outline" onClick={() => onCloseAccount(id)}>
              Закрыть счёт
            </Button>
          </div>
        );
      },
    },
  ];
}

/** Read-only tabs migrated to SmartTable (no inline edit, no action column). */
export const READONLY_TAB_COLUMNS: Record<string, ColumnDef<TabRow>[]> = {
  "Cash Flow": CASHFLOW_COLUMNS,
  "Exchange Rates": EXCHANGE_RATE_COLUMNS,
  Accounts: ACCOUNT_COLUMNS,
};

